import type { AppState, AuthorizationParams, RedirectLoginOptions } from '@auth0/auth0-vue';

type GetAuth0LoginRedirectOptionsOptions = {
  forceFreshLogin?: boolean;
  screenHint?: AuthorizationParams['screen_hint'];
  customParams?: Record<string, string>;
  invitation?: OrganizationInvitationParams;
};

export type OrganizationInvitationParams = {
  invitation: string;
  organization: string;
};

const getScalarQueryParam = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

export const getOrganizationInvitationParams = (
  query: Record<string, unknown>,
): OrganizationInvitationParams | undefined => {
  const invitation = getScalarQueryParam(query.invitation);
  const organization = getScalarQueryParam(query.organization);

  if (!invitation || !organization) {
    return undefined;
  }

  return { invitation, organization };
};

export const getRedirectTargetQueryParams = (redirectTarget: string): Record<string, string> => {
  const pathWithQuery = redirectTarget.split('#', 1)[0] ?? '';
  const queryIndex = pathWithQuery.indexOf('?');

  if (queryIndex === -1) {
    return {};
  }

  return Object.fromEntries(new URLSearchParams(pathWithQuery.slice(queryIndex + 1)));
};

export const getAuth0LoginRedirectOptions = (
  organizationId?: string,
  options?: GetAuth0LoginRedirectOptionsOptions,
): RedirectLoginOptions<AppState> | undefined => {
  const authorizationParams: AuthorizationParams = {
    ...options?.customParams,
    ...options?.invitation,
    ...(organizationId
      ? {
          organization: organizationId,
        }
      : {}),
    ...(options?.forceFreshLogin
      ? {
          prompt: 'login',
          max_age: 0,
        }
      : {}),
    ...(options?.screenHint
      ? {
          screen_hint: options.screenHint,
        }
      : {}),
  };

  if (Object.keys(authorizationParams).length === 0) {
    return undefined;
  }

  return {
    authorizationParams,
  };
};
